import {
  Permission,
  PermissionsEnum,
  PermissionLevel,
} from "@/modules/common/types";
import { LeadSalesLabel } from "@/modules/lead-page/types";
import { Lead } from "@/modules/leads/types";
import { formatDate, formatDateWithoutTime } from "@/modules/leads/utils";
import UserTag from "@/modules/user/components/user-tag";
import LeadTagsChips from "@/modules/leads/components/leads-table/components/lead-tags-chips";
import { muiTheme } from "@/styles/theme";

export const leadsTableColumns = (hasPermission: boolean) => [
  {
    Header: "ID",
    accessor: "id",
    minWidth: 160,
    sortingAccessor: "id",
  },
  {
    Header: "Name",
    accessor: "name",
    minWidth: 120,
    sortingAccessor: "name",
  },

  {
    Header: "Telephone",
    accessor: "customer.lead_telephones[0].telephone",
    minWidth: 120,
    sortingAccessor: "telephone",
  },
  {
    Header: "Lead Type",
    accessor: "lead_type.lead_type",
    minWidth: 80,
    sortingAccessor: "leadType",
  },
  {
    Header: "Date of Last Request",
    accessor: (row: Lead) => formatDate(row.date_of_last_request),
    minWidth: 170,
    sortingAccessor: "dateOfLastRequest",
  },
  {
    Header: "Method of Contact",
    accessor: "method_of_contact.name",
    minWidth: 150,
    sortingAccessor: "methodOfContact",
  },
  {
    Header: "Lead Source",
    accessor: "lead_source.name",
    minWidth: 130,
    sortingAccessor: "leadSource",
  },
  ...(!hasPermission
    ? [
        {
          Header: "Campaign",
          accessor: (row: Lead) =>
            row.campaign?.name && row.campaign?.name.length > 20
              ? `${row.campaign?.name.substring(0, 20)}...`
              : row.campaign?.name,
          minWidth: 120,
          sortingAccessor: "campaign",
        },
        {
          Header: "Adset",
          accessor: (row: Lead) =>
            row.adset?.name && row.adset?.name.length > 20
              ? `${row.adset?.name.substring(0, 20)}...`
              : row.adset?.name,
          minWidth: 120,
          sortingAccessor: "adset",
        },
        {
          Header: "Ad",
          accessor: (row: Lead) =>
            row.ad?.name && row.ad?.name.length > 20
              ? `${row.ad?.name.substring(0, 20)}...`
              : row.ad?.name,
          minWidth: 120,
          sortingAccessor: "ad",
        },
        {
          Header: "Adterm",
          accessor: "adterm.name",
          minWidth: 120,
          sortingAccessor: "adterm",
        },
      ]
    : []),
  {
    Header: "Organization",
    accessor: "organization.name",
    minWidth: 120,
    sortingAccessor: "organization",
  },
  {
    Header: "Reassign",
    accessor: (row: Lead) => (row.reassigned ? "Yes" : "No"),
    minWidth: 120,
    sortingAccessor: "reassign",
  },
  {
    Header: "Status",
    accessor: "status.status",
    minWidth: 180,
    sortingAccessor: "status",
  },
  {
    Header: "Current Status Stage",
    accessor: "status.status_category.name",
    minWidth: 170,
    sortingAccessor: "statusCategory",
  },
  {
    Header: "Next Status Stage",
    accessor: "status.next_status_category.name",
    minWidth: 170,
    sortingAccessor: "nextStatusCategory",
  },
  {
    Header: "Location",
    accessor: "location",
    minWidth: 120,
    sortingAccessor: "location",
  },
  {
    Header: "New Location",
    accessor: "new_location",
    minWidth: 120,
    sortingAccessor: "newLocation",
  },
  {
    Header: "Date of Last Contact",
    accessor: (row: Lead) =>
      row.date_of_last_contact && formatDate(row.date_of_last_contact),
    minWidth: 170,
    sortingAccessor: "dateOfLastContact",
  },
  {
    Header: "Sales Agent",
    accessor: "user.name",
    minWidth: 250,
    sortingAccessor: "assignedUser",
  },
  {
    Header: "Created At",
    accessor: (row: Lead) => formatDate(row.created_at),
    minWidth: 140,
    sortingAccessor: "createdAt",
  },
  {
    Header: "Broker",
    accessor: "broker",
    minWidth: 120,
    sortingAccessor: "broker",
  },
  {
    Header: "Parked",
    accessor: (row: Lead) => (row?.parked ? "Yes" : "No"),
    minWidth: 120,
    sortingAccessor: "parked",
  },
  {
    Header: "Invalid Client",
    accessor: (row: Lead) => (row?.rubbish ? "Yes" : "No"),
    minWidth: 120,
    sortingAccessor: "rubbish",
  },
  {
    Header: "Database",
    accessor: (row: Lead) => (row.database ? "Yes" : "No"),
    minWidth: 120,
    sortingAccessor: "database",
  },

  {
    Header: "Previous Status",
    accessor: (row: Lead) => {
      return row.previous_status?.name;
    },
    minWidth: 120,
  },
  {
    Header: "Previous User",
    accessor: "previous_user.name",
    minWidth: 180,
  },
  {
    Header: "Referral Expiration Date",
    accessor: (row: Lead) => {
      const referral = !Array.isArray(row?.referral) ? row?.referral : null;
      const date = referral?.validityExpirationDate;

      if (!date) return "-";
      return formatDateWithoutTime(date);
    },
    minWidth: 120,
  },
  {
    Header: "Referred From",
    accessor: (row: Lead) => (
      <UserTag
        name={
          !Array.isArray(row?.referral) ? row?.referral?.referrer?.name : ""
        }
        color={
          !Array.isArray(row?.referral) ? row?.referral?.referrer?.color : ""
        }
      />
    ),
    minWidth: 120,
    sortingAccessor: "referralValidity",
  },
  {
    Header: "Lead Tags",
    accessor: (row: Lead) => <LeadTagsChips tags={row.leadTags} />,
    minWidth: 200,
  },
];

export const defaultColumns = (hasPermission: boolean) => {
  return [
    "id",
    "name",
    "mark_as_done",
    "customer.lead_telephones[0].telephone",
    "Date of Last Request",
    "method_of_contact.name",
    ...(!hasPermission
      ? ["campaign.name", "adset.name", "ad.name", "adterm.name"]
      : []),
    "adset.name",
    "ad.name",
    "organization.name",
    "nanoId",
    "status.status",
    "location",
    "Date of Last Contact",
    "user.name",
  ];
};

interface GroupMappings {
  [key: number]: Permission[];
}

export const groupMappings: GroupMappings = {
  100: [
    {
      name: PermissionsEnum.PAGINATION_100,
      level: PermissionLevel.READ,
    },
  ],
  200: [
    {
      name: PermissionsEnum.PAGINATION_200,
      level: PermissionLevel.READ,
    },
  ],
  300: [
    {
      name: PermissionsEnum.PAGINATION_300,
      level: PermissionLevel.READ,
    },
  ],
  400: [
    {
      name: PermissionsEnum.PAGINATION_400,
      level: PermissionLevel.READ,
    },
  ],
  500: [
    {
      name: PermissionsEnum.PAGINATION_500,
      level: PermissionLevel.READ,
    },
  ],
};

export const leadSalesLabels: LeadSalesLabel[] = [
  { key: "property_type", label: "Property Type", type: "string" },
  { key: "unit_value", label: "Unit Value", type: "money" },
  { key: "compound", label: "Compound", type: "string" },
  { key: "developer", label: "Developer", type: "string" },
  { key: "date_of_reservation", label: "Date of Reservation", type: "date" },
  { key: "actual_value", label: "Actual Amount", type: "money" },
  { key: "sales_agent", label: "Sales Agent", type: "string" },
  { key: "sale_category", label: "Sale Category", type: "string" },
  { key: "unit_number", label: "Unit Number", type: "string" },
  { key: "unit_location", label: "Unit Location", type: "string" },
  { key: "date_of_contraction", label: "Date of Contraction", type: "date" },
  { key: "linked_sale_type", label: "Linked Sale Type", type: "string" },
];

export const saleTypeMapping = new Map<number, string>([
  [1, "Contracted"],
  [2, "Reserved"],
  [3, "Blocked"],
  [4, "EOI"],
  [5, "Potential"],
]);
export const defaultLeadsTableColumns = (hasPermission: boolean) => [
  "id",
  "name",
  "customer.lead_telephones[0].telephone",
  "Date of Last Request",
  "method_of_contact.name",
  "lead_source.name",
  ...(!hasPermission
    ? ["campaign.name", "adset.name", "ad.name", "adterm.name"]
    : []),
  "adset.name",
  "ad.name",
  "status.status",
  "location",
  "Date of Last Contact",
  "user.name",
];

export const defaultNextActionsTableColumns = [
  "id",
  "ID",
  "nanoId",
  "name",
  "mark_as_done",
  "customer.lead_telephones[0].telephone",
  "Date of Last Request",
  "method_of_contact.name",
  "lead_source.name",
  "campaign.name",
  "adset.name",
  "ad.name",
  "status.status",
  "location",
  "Date of Last Contact",
  "user.name",
];

export const paginationArrowButtonStyles = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "32px",
  height: "38px",
  border: "solid 1px #d0d5dd",
  borderRadius: "8px",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "#d5eaf2",
    cursor: "pointer",
  },
  "&:active": {
    boxShadow: "0px 0px 0px 4px #f2f4f7",
    zIndex: 1,
  },
};

export const pageButtonStyles = {
  maxWidth: "40px",
  minWidth: "40px",
  height: "36px",
  border: "solid 1px #d0d5dd",
  transition: "background-color 0.2s",
  color: muiTheme.colorSchemes.light.palette.main.dark,
  "&:active": {
    boxShadow: "0px 0px 0px 4px #f2f4f7",
    zIndex: 1,
  },
};

export const commonClearAllButtonStyles = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: 1,
  borderRadius: 2,
  maxWidth: "160px",
  maxHeight: "40px",
  minWidth: "125px",
  border: `1px solid ${muiTheme.colorSchemes.light.palette.warning[600]}`,
  color: muiTheme.colorSchemes.light.palette.warning[600],
  background: muiTheme.colorSchemes.light.palette.main.white,
  boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
  ...muiTheme.typography.h5_med,
  textTransform: "none",
  "&:hover": {
    color: muiTheme.colorSchemes.light.palette.warning[600],
    background: "#fff6ea",
    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
  },
  "&:active": {
    boxShadow:
      "0px 0px 0px 4px #F2F4F7, 0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
  },
};

export const commonSearchButtonStyles = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: 1,
  borderRadius: 2,
  maxHeight: "40px",
  maxWidth: "160px",
  minWidth: "95px",
  color: muiTheme.colorSchemes.light.palette.background.white,
  background: muiTheme.colorSchemes.light.palette.primary[500],
  ...muiTheme.typography.h5_med,
  textTransform: "none",
  textWrap: "nowrap",
  boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
  "&:hover": {
    color: muiTheme.colorSchemes.light.palette.background.white,
    background: "#034462",
    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
  },
};

export enum LeadsTableHeaders {
  ID = "ID",
  NAME = "Name",
  TELEPHONE = "Telephone",
  LEAD_TYPE = "Lead Type",
  DATE_OF_LAST_REQUEST = "Date of Last Request",
  METHOD_OF_CONTACT = "Method of Contact",
  LEAD_SOURCE = "Lead Source",
  CAMPAIGN = "Campaign",
  ADSET = "Adset",
  AD = "Ad",
  ADTERM = "Adterm",
  REASSIGN = "Reassign",
  BEST_TIME_TO_CALL = "Best Time to Call",
  STATUS = "Status",
  STATUS_CATEGORY = "Current Status Stage",
  NEXT_STATUS_CATEGORY = "Next Status Stage",
  LOCATION = "Location",
  NEW_LOCATION = "New Location",
  DATE_OF_LAST_CONTACT = "Date of Last Contact",
  SALES_AGENT = "Sales Agent",
  CREATED_AT = "Created At",
  BROKER = "Broker",
  PARKED = "Parked",
  RUBBISH = "Rubbish",
  DATABASE = "Database",
  PREVIOUS_USER = "Previous User",
  PREVIOUS_STATUS = "Previous Status",
  REFERRED_FROM = "Referred From",
  LEAD_TAGS = "Lead Tags",
}

export enum SaleDocumentType {
  PAYMENT_PLAN = "paymentPlanFiles",
  PAYMENT = "paymentFiles",
  FILES = "files",
}
